import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { endpoints } from "@/lib/api";
import QRCode from "react-qr-code";
import { CalendarDays, MapPin, Ticket, X } from "lucide-react";
import { formatDateLong } from "@/lib/format";

const STATUS_LABEL = {
    unpaid: "Advance",
    paid: "Paid externally",
    pay_at_door: "Pay at door",
};

export default function EventPassDialog({ registrationId, open, onOpenChange }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !registrationId) return;
        setLoading(true);
        endpoints
            .getPass(registrationId)
            .then((d) => {
                setData(d);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [open, registrationId]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="bg-[#0a0a0a] border-[#2a2a2a] rounded-none max-w-md p-0 overflow-hidden"
                data-testid="event-pass-dialog"
            >
                <DialogHeader className="px-5 pt-5 pb-0">
                    <DialogTitle className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">
                        Event Pass
                    </DialogTitle>
                    <DialogDescription className="sr-only">
                        Digital registration pass with QR code, fan name, tier, and payment status. Show at the venue door.
                    </DialogDescription>
                </DialogHeader>

                {loading || !data ? (
                    <div className="p-8 font-mono text-sm text-[#666]" data-testid="event-pass-loading">
                        Loading pass…
                    </div>
                ) : (
                    <div>
                        {/* Event header */}
                        <div className="px-5 pb-4 border-b border-[#2a2a2a]">
                            <div className="font-display font-black uppercase text-xl leading-tight" data-testid="pass-event-name">
                                {data.event?.name}
                            </div>
                            {data.artist && (
                                <div className="mt-1 text-[10px] font-mono uppercase text-[#a3a3a3]">
                                    {data.artist.name}
                                </div>
                            )}
                            <div className="mt-2 flex flex-wrap gap-3 text-xs text-[#a3a3a3]">
                                <span className="flex items-center gap-1">
                                    <CalendarDays className="w-3 h-3" /> {formatDateLong(data.event?.date)} · {data.event?.start_time}
                                </span>
                                <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> {data.event?.venue_name} · {data.event?.city}
                                </span>
                            </div>
                        </div>

                        {/* QR */}
                        <div className="px-5 py-6 flex justify-center bg-white" data-testid="pass-qr-container">
                            <QRCode
                                value={data.qr_payload}
                                size={240}
                                level="M"
                                bgColor="#ffffff"
                                fgColor="#0a0a0a"
                            />
                        </div>

                        {/* Fan + status */}
                        <div className="px-5 py-4 grid grid-cols-2 gap-3 border-t border-[#2a2a2a]">
                            <PassField label="Fan" value={data.registration?.fan_name} testId="pass-fan-name" />
                            <PassField label="Tier" value={data.registration?.ticket_category_name || "General"} testId="pass-ticket-category" />
                            <PassField label="Payment" value={data.event?.is_free ? "Free" : STATUS_LABEL[data.registration?.payment_status] || data.registration?.payment_status} testId="pass-payment-status" />
                            <PassField label="Status" value={data.registration?.checked_in ? "Checked in" : "Registered"} testId="pass-status" highlight={data.registration?.checked_in ? "green" : "orange"} />
                        </div>

                        <div className="px-5 pb-5 pt-1 text-[10px] font-mono uppercase tracking-widest text-[#666]" data-testid="pass-disclaimer">
                            Registration pass · Show at door · Project Mint does not process payments
                        </div>

                        {!data.event?.is_free && data.registration?.payment_status === "pay_at_door" && (
                            <div className="mx-5 mb-5 border border-[#2a2a2a] p-3 text-xs text-[#a3a3a3] leading-relaxed" data-testid="pass-pay-at-door-copy">
                                Pay at the door via cash, mobile money, or bank transfer. Contact the event organizer for advance payment options.
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function PassField({ label, value, testId, highlight }) {
    const color = highlight === "green" ? "text-[#00e676]" : highlight === "orange" ? "text-[#ff4400]" : "text-[#f4f4f0]";
    return (
        <div data-testid={testId}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">{label}</div>
            <div className={`mt-1 font-display font-bold uppercase text-sm ${color}`}>{value || "—"}</div>
        </div>
    );
}
